/**
 * Unified Recipe Service
 *
 * Manages hybrid recipe loading from:
 * 1. Shared recipes (Firestore `recipes/` collection) - 498 base recipes for all users
 * 2. User's saved recipes (Firestore `users/{userId}/savedRecipes/`) - Chrome extension saves
 * 3. User preferences (hidden recipes, favorites)
 *
 * Provides deduplication when saving new recipes.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  arrayUnion,
  arrayRemove,
  deleteField,
} from 'firebase/firestore';
import { Platform } from 'react-native';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { Recipe, RecipeInstructionSection, RecipeReviewStatus } from '../types/recipe';
import { resolveDataScope, buildScopedPath } from './householdPaths';
import { getActiveHouseholdId } from './householdContext';
import {
  purgeRecipeReferences,
  resolveAllUserScopes,
  type ReferenceCleanupResult,
} from './recipeReferenceCleanup';
import { coerceInstructionSections, normalizeInstructions } from '../utils/normalizeInstructions';
import { hasBareStepNumbers, isGenericStepsLabel } from '../utils/stepNumbering';
import { isInLibrary } from '../utils/libraryVisibility';
import { catalogueIndexEntry, savedIndexEntry, mergeRecipeLibrary, normalizeTitle } from './recipeLibraryCore';
import { indexIngredientLines } from '../utils/recipeContent';
import { ingredientRowsToStore } from '../utils/ingredientEditLines';
import { recipeLanguageField } from '../utils/languageDetection';
import { requestTranslationAtWrite } from './recipeTranslationRequest';
import type { RecipeTimeline, TimelineStatus } from '../utils/recipeTimeline';

function deepRemoveUndefined<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(deepRemoveUndefined) as unknown as T;
  if (obj !== null && typeof obj === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (v !== undefined) out[k] = deepRemoveUndefined(v);
    }
    return out as T;
  }
  return obj;
}

/**
 * Path helpers — route savedRecipes + preferences through the active scope
 * (household when paired, else user-only).
 */
const getSavedRecipesCollection = (userId: string) => {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  const scope = resolveDataScope(userId, getActiveHouseholdId());
  if (!scope) {
    throw new Error('Cannot resolve data scope');
  }
  const [root, id, ...rest] = buildScopedPath(scope, 'savedRecipes');
  return collection(firestore, root, id, ...rest);
};

const getSavedRecipeDocRef = (userId: string, recipeId: string) => {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  const scope = resolveDataScope(userId, getActiveHouseholdId());
  if (!scope) {
    throw new Error('Cannot resolve data scope');
  }
  const [root, id, ...rest] = buildScopedPath(
    scope,
    'savedRecipes',
    recipeId
  );
  return doc(firestore, root, id, ...rest);
};

const getRecipePreferencesDocRef = (userId: string) => {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  const scope = resolveDataScope(userId, getActiveHouseholdId());
  if (!scope) {
    throw new Error('Cannot resolve data scope');
  }
  const [root, id, ...rest] = buildScopedPath(
    scope,
    'preferences',
    'recipes'
  );
  return doc(firestore, root, id, ...rest);
};

// Branded fallback shown when a recipe's image is missing or points to the
// legacy `images/xxx.jpg` mirror that wasn't shipped to production. The
// public catalog has ~61 such recipes whose source pages are paywalled,
// dead, or have no og:image; the placeholder makes them obviously
// non-photographic instead of rendering as a broken thumbnail.
// PNG (not SVG) so React Native's <Image source={{ uri }}> renders it
// reliably on web — RN Web's Image rasterizes via background-image,
// which doesn't always handle SVG URIs cleanly.
const PLACEHOLDER_IMAGE_WEB = '/assets/recipes/placeholder.png';

function fixImagePath(imagePath?: string): string | undefined {
  // Normalize http → https to avoid mixed-content blocks on HTTPS sites
  if (imagePath && (imagePath.startsWith('http://') || imagePath.startsWith('https://'))) {
    return imagePath.replace(/^http:\/\//, 'https://');
  }

  const isLegacyMirror = !!imagePath && imagePath.startsWith('images/');
  const needsPlaceholder = !imagePath || isLegacyMirror;

  if (Platform.OS === 'web') {
    if (needsPlaceholder) return PLACEHOLDER_IMAGE_WEB;
    if (imagePath!.startsWith('/assets/')) return imagePath;
    return imagePath;
  }

  // Native: bundled placeholder assets aren't addressable by URI string,
  // so return undefined for unrenderable paths. Consumers already handle
  // `!recipe.image` by rendering a local fallback view.
  return needsPlaceholder ? undefined : imagePath;
}

// Types for Firestore documents
export interface SharedRecipe {
  id: string;
  title: string;
  normalizedTitle: string;
  image?: string;
  servings: number;
  servingsDisplay: string;
  readyInMinutes: number;
  cookingMinutes?: number;
  preparationMinutes?: number;
  handsOnMinutes?: number;
  readyInMinutesMin?: number;
  statedReadyInMinutes?: number;
  timeline?: RecipeTimeline;
  timelineStatus?: TimelineStatus;

  ingredients: string[];
  extendedIngredients: ExtendedIngredient[];
  instructions: Instruction[];
  instructionSections?: InstructionSection[];  // Sectioned instructions with headers
  stepIngredients?: { step: string; ingredients: number[] }[];  // Resolved step→ingredient map
  ingredientGroups?: { name: string; items: string[] }[];  // Sectioned ingredients with headers

  cuisines: string[];
  dishTypes: string[];
  diets: string[];
  nutrition?: Nutrition;
  summary?: string;
  description?: string;

  sourceUrl?: string;
  sourceName?: string;
  originalLanguage?: 'en' | 'fr';
  /** Languages the recipe is fully readable in; see api/_lib/translationReadiness. */
  languages?: ('en' | 'fr')[];
  searchText: string;

  videoUrl?: string;
  videoPlatform?: 'instagram' | 'facebook' | 'youtube' | 'tiktok';
  videoThumbnail?: string;

  migratedAt?: string;
  isBaseRecipe?: boolean;
  lastScrapedAt?: string;
  sourceRating?: number;

  // Collection-import review workflow (see RecipeReviewStatus in types/recipe).
  reviewStatus?: RecipeReviewStatus;
  importBatchId?: string;
  importBatchTitle?: string;
  importBatchSourceUrl?: string;
  importedVia?: string;
  publishedAt?: string;
}

export interface UserSavedRecipe extends SharedRecipe {
  savedAt: string;
  source: 'chrome-extension' | 'app' | 'email-import';
  userId: string;
  newsletterName?: string;
}

export interface UserPreferences {
  hidden: string[];
  favorites: string[];
}

interface ExtendedIngredient {
  original: string;
  normalized: string;
  name: string;
  amount?: number;
  unit?: string;
  aisle?: string;
  image?: string;
  meta?: string[];
  measures?: unknown;
}

interface Instruction {
  number: number;
  step: string;
}

interface InstructionSection {
  name?: string;
  steps: Instruction[];
}

interface Nutrition {
  calories?: number;
  protein?: number;
  totalFat?: number;
  totalCarbohydrate?: number;
  sodium?: number;
  fiber?: number;
  sugars?: number;
}

export interface RecipeIndexEntry {
  id: string;
  title: string;
  image?: string;
  servings: number;
  servingsDisplay: string;
  readyInMinutes: number;
  /** The two derived numbers only: the timeline itself stays off the persisted index. */
  handsOnMinutes?: number;
  readyInMinutesMin?: number;
  dishTypes: string[];
  cuisines: string[];
  diets: string[];
  ingredients: string[];
  searchText: string;
  originalLanguage?: 'en' | 'fr';
  // For imported/saved recipes
  savedAt?: string;
  source?: 'chrome-extension' | 'app' | 'email-import' | 'base';
  newsletterName?: string;
  // For enrichment
  sourceUrl?: string;
  lastScrapedAt?: string;
  enrichmentAttemptCount?: number;
  // Source website rating
  sourceRating?: number;
  sourceRatingCount?: number;
  translatedTitles?: { en?: string; fr?: string };
  /** Languages the recipe is fully readable in; the discovery gate reads this (#737). */
  languages?: ('en' | 'fr')[];
}

export interface SaveRecipeResult {
  saved: boolean;
  duplicate?: RecipeIndexEntry;
  error?: string;
}

export { normalizeTitle };

/**
 * One shared-catalog document, as the recipe index sees it — the field list
 * lives in `recipeLibraryCore.ts`, shared with the weekly email.
 */
export function toSharedIndexEntry(data: SharedRecipe): RecipeIndexEntry {
  return catalogueIndexEntry(data, fixImagePath);
}

/**
 * Reconstruct instruction sections from a flat instruction list that contains
 * section headers mixed in as plain-text entries (common in older Firestore
 * recipes scraped before instructionSections was persisted).
 *
 * A "header" is detected by: short (\u226440 chars), no digits, no trailing
 * sentence punctuation, no verb suffix (-ons/-ez/-er/-ant), Title Case start.
 * Returns undefined when no headers are found (flat list stays flat).
 *
 * Two cases go to normalizeInstructions instead, the rule the recipe screen and
 * cooking mode apply to a flat list (#794):
 *  - a list the publisher numbered ("1Dans un bol", "5 Pour le BBQ:"). The
 *    digit test below can never see its labels, and the numbers stay on;
 *  - a leading « Étapes à suivre » / « Préparation », which titles the list
 *    rather than opening a section of it. Alone, it made one section named
 *    after the list.
 */
function reconstructSections(
  allInstructions: Array<{ number: number; step: string }>
): RecipeInstructionSection[] | undefined {
  if (hasBareStepNumbers(allInstructions.map(i => i.step))) {
    const normalized = normalizeInstructions(allInstructions);
    return normalized.hasSections
      ? normalized.sections.map(s => ({ name: s.name, steps: s.steps }))
      : undefined;
  }
  const instructions = isGenericStepsLabel(allInstructions[0]?.step ?? '')
    ? allInstructions.slice(1)
    : allInstructions;

  const HEADER_RE = /^[A-Z\u00c0-\u00dcA-Z][a-z\u00e0-\u00fc\u00c0-\u00ff]*([\s-][A-Za-z\u00e0-\u00fc\u00c0-\u00ff]+){0,4}$/u;
  const isHeader = (s: string): boolean => {
    const t = s.trim();
    if (t.length > 40) return false;
    if (/\d/.test(t)) return false;
    if (/[.!?\u2026,:;]$/.test(t)) return false;
    // Ends with a verb suffix \u2192 it's a sentence, not a header label
    if (/(ons|ez|er|ant|aient)$/i.test(t)) return false;
    // First word is an imperative/infinitive verb (e.g. "Ajoutez", "Incorporer")
    const firstWord = t.split(/\s+/)[0];
    if (/(ez|ons|er|ant)$/i.test(firstWord)) return false;
    return HEADER_RE.test(t);
  };

  if (!instructions.some((i) => isHeader(i.step))) return undefined;

  const sections: RecipeInstructionSection[] = [];
  let current: { name?: string; steps: Array<{ number: number; step: string }> } = { steps: [] };

  for (const inst of instructions) {
    if (isHeader(inst.step)) {
      if (current.steps.length > 0 || current.name) {
        sections.push({ name: current.name, steps: current.steps });
      }
      current = { name: inst.step, steps: [] };
    } else {
      // Merge continuation fragments: if the previous step doesn't end with
      // sentence-final punctuation and this one starts lowercase, append it.
      const prev = current.steps[current.steps.length - 1];
      if (prev && !/[.!?\u2026]$/.test(prev.step) && /^[a-z\u00e0-\u00fc\u00e0-\u00ff]/.test(inst.step)) {
        prev.step = prev.step + ' ' + inst.step;
      } else {
        current.steps.push({ number: current.steps.length + 1, step: inst.step });
      }
    }
  }
  if (current.steps.length > 0 || current.name) {
    sections.push({ name: current.name, steps: current.steps });
  }

  return sections.length > 0 ? sections : undefined;
}

/** The cache scope of a read that saw the shared catalogue and nothing else. */
const SHARED_SCOPE = 'shared';

/**
 * What one `getRecipeById` cost (#874): the documents it read, the round trips
 * it waited for, and whether a cached or in-flight read answered instead. The
 * recipe screen copies it onto `recipe_loaded`.
 */
export interface RecipeReadTrace {
  reads: number;
  roundTrips: number;
  cache: 'hit' | 'joined' | 'miss' | null;
}

export const newRecipeReadTrace = (): RecipeReadTrace => ({ reads: 0, roundTrips: 0, cache: null });

class UnifiedRecipeService {
  // Cache for recipes
  private sharedRecipesCache: RecipeIndexEntry[] | null = null;
  private userRecipesCache: Map<string, RecipeIndexEntry[]> = new Map();
  private preferencesCache: Map<string, UserPreferences> = new Map();
  private lastCacheTime = 0;
  private readonly CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

  // Cache for full recipe details (LRU-style). An entry remembers the scope it
  // was read in: one read before the household resolved never saw the
  // household's copy, so it cannot answer once the household is known.
  // 200 because the planner warms the days ahead (recipePrefetch.ts), and a
  // recipe is a few KB.
  private recipeDetailCache = new Map<string, { recipe: Recipe; timestamp: number; scope: string }>();
  private readonly DETAIL_CACHE_SIZE = 200;
  /** Reads in flight, so an open joins the prefetch that started it (#874). */
  private detailReads = new Map<string, Promise<Recipe | null>>();
  /** Bumped by every invalidation: a read that started before it is not cached. */
  private detailGeneration = 0;

  /**
   * Check if cache is valid
   */
  private isCacheValid(): boolean {
    return Date.now() - this.lastCacheTime < this.CACHE_EXPIRY_MS;
  }

  /**
   * Invalidate all caches
   */
  invalidateCache(): void {
    this.sharedRecipesCache = null;
    this.userRecipesCache.clear();
    this.preferencesCache.clear();
    this.recipeDetailCache.clear();
    this.detailReads.clear();
    this.detailGeneration += 1;
    this.lastCacheTime = 0;
  }

  /**
   * Which copies a detail read could see: the cook and their household. A
   * guest sees the catalogue alone, the same thing `getSharedRecipeById` reads.
   */
  private detailScope(userId?: string): string {
    return userId ? `${userId}|${getActiveHouseholdId() ?? ''}` : SHARED_SCOPE;
  }

  /**
   * Cache a recipe detail with LRU eviction
   */
  private cacheRecipeDetail(recipeId: string, recipe: Recipe, scope: string): void {
    // Remove if already exists (to update position in Map for LRU)
    if (this.recipeDetailCache.has(recipeId)) {
      this.recipeDetailCache.delete(recipeId);
    }

    // Add new entry
    this.recipeDetailCache.set(recipeId, {
      recipe,
      timestamp: Date.now(),
      scope,
    });

    // Evict oldest entries if cache exceeds limit
    if (this.recipeDetailCache.size > this.DETAIL_CACHE_SIZE) {
      const firstKey = this.recipeDetailCache.keys().next().value;
      if (firstKey) {
        this.recipeDetailCache.delete(firstKey);
      }
    }
  }

  /**
   * Get cached recipe detail if valid
   */
  private getCachedRecipeDetail(recipeId: string, scope?: string): Recipe | null {
    const cached = this.recipeDetailCache.get(recipeId);
    if (scope !== undefined && cached?.scope !== scope) return null;
    if (cached && Date.now() - cached.timestamp < this.CACHE_EXPIRY_MS) {
      console.log(`UnifiedRecipeService - Using cached recipe: ${recipeId}`);
      return cached.recipe;
    }
    return null;
  }

  /**
   * Get all recipes for a user (shared + personal, excluding hidden)
   */
  async getAllRecipes(userId?: string): Promise<RecipeIndexEntry[]> {
    if (!isFirebaseEnabled() || !firestore) {
      console.warn('UnifiedRecipeService - Firebase not enabled');
      return [];
    }

    try {
      // Get shared recipes
      const shared = await this.getSharedRecipes();

      // Get user's saved recipes
      const personal = userId ? await this.getUserSavedRecipes(userId) : [];

      // Get hidden recipe IDs
      const hidden = userId ? await this.getHiddenRecipeIds(userId) : [];

      // Merge and dedupe
      return this.mergeAndDedupe(shared, personal, hidden);
    } catch (error) {
      console.error('UnifiedRecipeService - Error getting all recipes:', error);
      return [];
    }
  }

  /**
   * Build the savedRecipes collection path under the active scope
   * (household when paired, else users/{uid}/savedRecipes).
   */
  private getSavedRecipesCollectionPath(userId: string): string {
    const householdId = getActiveHouseholdId();
    const segments = householdId
      ? ['households', householdId, 'savedRecipes']
      : ['users', userId, 'savedRecipes'];
    return segments.join('/');
  }

  /**
   * Get shared recipes from Firestore.
   *
   * Ungated on purpose: the result is persisted as the recipe index and
   * outlives a language switch, so the reader's gate (#737) is applied on read
   * by `useReadableRecipeIndex` / `isOfferedTo`, never here.
   */
  async getSharedRecipes(): Promise<RecipeIndexEntry[]> {
    if (this.sharedRecipesCache && this.isCacheValid()) {
      return this.sharedRecipesCache;
    }

    if (!isFirebaseEnabled() || !firestore) {
      return [];
    }

    try {
      const recipesRef = collection(firestore, 'recipes');
      const snapshot = await getDocs(recipesRef);

      const recipes: RecipeIndexEntry[] = snapshot.docs.map((doc) =>
        toSharedIndexEntry(doc.data() as SharedRecipe));

      this.sharedRecipesCache = recipes;
      this.lastCacheTime = Date.now();
      console.log(`UnifiedRecipeService - Loaded ${recipes.length} shared recipes`);
      return recipes;
    } catch (error: any) {
      if (error?.code === 'permission-denied') {
        console.error(
          'UnifiedRecipeService - Permission denied reading shared recipes. ' +
          'Check firestore.rules allows reads on /recipes.'
        );
      } else {
        console.error('UnifiedRecipeService - Error fetching shared recipes:', error);
      }
      return [];
    }
  }

  /**
   * Get user's saved recipes from Firestore
   */
  async getUserSavedRecipes(userId: string): Promise<RecipeIndexEntry[]> {
    if (this.userRecipesCache.has(userId) && this.isCacheValid()) {
      return this.userRecipesCache.get(userId)!;
    }

    if (!isFirebaseEnabled() || !firestore) {
      return [];
    }

    try {
      const savedRef = getSavedRecipesCollection(userId);
      const q = query(savedRef, orderBy('savedAt', 'desc'));
      const snapshot = await getDocs(q);

      // Roundup recipes still waiting for review, or turned down, are not part of
      // the library: the collection is shared with the whole household.
      const recipes: RecipeIndexEntry[] = snapshot.docs
        .filter((doc) => isInLibrary(doc.data() as UserSavedRecipe))
        .map((doc) => savedIndexEntry(doc.id, doc.data() as UserSavedRecipe, fixImagePath));

      this.userRecipesCache.set(userId, recipes);
      console.log(`UnifiedRecipeService - Loaded ${recipes.length} saved recipes for user`);
      return recipes;
    } catch (error) {
      console.error('UnifiedRecipeService - Error fetching user recipes:', error);
      return [];
    }
  }

  /**
   * Get user preferences (hidden, favorites)
   */
  async getUserPreferences(userId: string): Promise<UserPreferences> {
    if (this.preferencesCache.has(userId) && this.isCacheValid()) {
      return this.preferencesCache.get(userId)!;
    }

    if (!isFirebaseEnabled() || !firestore) {
      return { hidden: [], favorites: [] };
    }

    try {
      const prefsRef = getRecipePreferencesDocRef(userId);
      const snapshot = await getDoc(prefsRef);

      if (snapshot.exists()) {
        const data = snapshot.data() as UserPreferences;
        this.preferencesCache.set(userId, data);
        return data;
      }

      return { hidden: [], favorites: [] };
    } catch (error) {
      console.error('UnifiedRecipeService - Error fetching preferences:', error);
      return { hidden: [], favorites: [] };
    }
  }

  /**
   * Get hidden recipe IDs for a user
   */
  async getHiddenRecipeIds(userId: string): Promise<string[]> {
    const prefs = await this.getUserPreferences(userId);
    return prefs.hidden;
  }

  /**
   * Get favorite recipe IDs for a user
   */
  async getFavoriteRecipeIds(userId: string): Promise<string[]> {
    const prefs = await this.getUserPreferences(userId);
    return prefs.favorites;
  }

  /**
   * Merge shared and personal recipes, excluding hidden ones
   */
  private mergeAndDedupe(
    shared: RecipeIndexEntry[],
    personal: RecipeIndexEntry[],
    hidden: string[]
  ): RecipeIndexEntry[] {
    return mergeRecipeLibrary(shared, personal, hidden);
  }

  /**
   * The shared catalogue's copy of a recipe, and nothing else: no savedRecipes
   * scope is read. For a caller that has already checked every savedRecipes
   * scope itself — `recipeService` walks household, personal and the legacy
   * importedRecipes before falling back here, and `getRecipeById` re-read the
   * first two (#777).
   */
  async getSharedRecipeById(recipeId: string): Promise<Recipe | null> {
    const cached = this.getCachedRecipeDetail(recipeId);
    if (cached) return cached;
    if (!isFirebaseEnabled() || !firestore) return null;
    try {
      const recipe = await this.readSharedRecipe(recipeId);
      if (recipe) this.cacheRecipeDetail(recipeId, recipe, SHARED_SCOPE);
      return recipe;
    } catch (error: any) {
      if (error?.code === 'permission-denied') return null;
      console.error('UnifiedRecipeService - Error fetching shared recipe:', error);
      return null;
    }
  }

  private async readSharedRecipe(recipeId: string): Promise<Recipe | null> {
    if (!firestore) return null;
    const sharedSnap = await getDoc(doc(firestore, 'recipes', recipeId));
    return sharedSnap.exists()
      ? this.convertToRecipe(sharedSnap.data() as SharedRecipe, recipeId)
      : null;
  }

  /**
   * Get full recipe details by ID.
   *
   * Every copy the cook could have is asked for at once: the active scope's
   * savedRecipes, the personal one when the active scope is the household (a
   * recipe saved while the household was still loading lands there), and the
   * shared catalogue. The first that exists, in that order, wins. It used to
   * read them one after another, so a catalogue recipe waited out two misses
   * before its own document was asked for: three round trips where one does
   * (#874). A saved recipe now costs one or two reads it did not, in the same
   * round trip.
   *
   * `trace` collects what the call cost, for `recipe_loaded`.
   */
  async getRecipeById(recipeId: string, userId?: string, trace?: RecipeReadTrace): Promise<Recipe | null> {
    const scope = this.detailScope(userId);
    const cached = this.getCachedRecipeDetail(recipeId, scope);
    if (cached) {
      if (trace) trace.cache = 'hit';
      return cached;
    }

    if (!isFirebaseEnabled() || !firestore) {
      return null;
    }

    // A prefetch already asking for it (recipePrefetch.ts): wait for that one.
    const key = `${recipeId}@${scope}`;
    const inFlight = this.detailReads.get(key);
    if (inFlight) {
      if (trace) trace.cache = 'joined';
      return inFlight;
    }
    if (trace) trace.cache = 'miss';

    const generation = this.detailGeneration;
    const read = this.readEveryCopy(recipeId, userId, trace)
      .then((recipe) => {
        if (recipe && generation === this.detailGeneration) {
          this.cacheRecipeDetail(recipeId, recipe, scope);
        }
        return recipe;
      })
      .catch((error: any) => {
        // Permission errors are expected when Firebase rules restrict access
        // - just return null silently and let caller use fallback
        if (error?.code !== 'permission-denied') {
          console.error('UnifiedRecipeService - Error fetching recipe:', error);
        }
        return null;
      })
      .finally(() => {
        if (this.detailReads.get(key) === read) this.detailReads.delete(key);
      });
    this.detailReads.set(key, read);
    return read;
  }

  /** One round trip over every document `getRecipeById` may answer with. */
  private async readEveryCopy(recipeId: string, userId: string | undefined, trace?: RecipeReadTrace): Promise<Recipe | null> {
    if (!firestore) return null;
    const refs = [];
    if (userId) {
      refs.push(getSavedRecipeDocRef(userId, recipeId));
      // The active scope is the household: also the cook's own collection.
      const altScope = getActiveHouseholdId() ? resolveDataScope(userId, null) : null;
      if (altScope) {
        const [root, id, ...rest] = buildScopedPath(altScope, 'savedRecipes', recipeId);
        refs.push(doc(firestore, root, id, ...rest));
      }
    }
    refs.push(doc(firestore, 'recipes', recipeId));
    if (trace) {
      trace.reads += refs.length;
      trace.roundTrips += 1;
    }

    // A scope the rules refuse (a household not joined yet) holds no copy for
    // this cook; any other failure fails the read, as the walk did.
    const snaps = await Promise.all(
      refs.map((ref) =>
        getDoc(ref).catch((error: any) => {
          if (error?.code === 'permission-denied') return null;
          throw error;
        })
      )
    );
    const found = snaps.find((snap) => snap?.exists());
    return found ? this.convertToRecipe(found.data() as SharedRecipe, recipeId) : null;
  }

  /**
   * A document this device just wrote, as a read of it would return it. Lets a
   * screen open a recipe it created without reading it back (#791).
   */
  recipeFromDocument(data: SharedRecipe, id: string): Recipe {
    return this.convertToRecipe(data, id);
  }

  /**
   * Convert Firestore document to Recipe type
   */
  /**
   * Builds extendedIngredients, healing legacy recipes that were saved with only a
   * simple `ingredients` string array (e.g. early photo imports) and no scrape
   * source. Gated on missing `sourceUrl` so web recipes still scrape for structured
   * data rather than rendering their raw strings.
   */
  private normalizeExtendedIngredients(data: SharedRecipe) {
    const toEntry = (
      name: string,
      original: string,
      idx: number,
      src?: { amount?: number; unit?: string; aisle?: string; image?: string; meta?: string[]; measures?: unknown },
    ) => ({
      id: idx,
      aisle: src?.aisle ?? '',
      image: src?.image ?? '',
      name,
      original,
      amount: src?.amount ?? 0,
      unit: src?.unit ?? '',
      meta: src?.meta ?? [],
      measures: src?.measures ?? {
        us: { amount: 0, unitShort: '', unitLong: '' },
        metric: { amount: 0, unitShort: '', unitLong: '' },
      },
    });

    if (data.extendedIngredients?.length) {
      return data.extendedIngredients.map((ing, idx) => toEntry(ing.name, ing.original, idx, ing));
    }
    if (!data.sourceUrl && Array.isArray(data.ingredients) && data.ingredients.length) {
      return data.ingredients.map((ing, idx) => toEntry(ing, ing, idx));
    }
    return [];
  }

  /**
   * Normalizes instructions to `{ number, step }`, tolerating both the structured
   * shape and legacy plain-string arrays (photo imports saved strings, which the old
   * mapping silently turned into `{ number: undefined, step: undefined }`).
   */
  private normalizeInstructions(instructions: unknown): Array<{ number: number; step: string }> {
    if (!Array.isArray(instructions)) return [];
    return instructions.map((inst, idx) => {
      if (typeof inst === 'string') return { number: idx + 1, step: inst };
      const obj = inst as { number?: number; step?: string };
      return { number: obj?.number ?? idx + 1, step: obj?.step ?? '' };
    });
  }

  private convertToRecipe(data: SharedRecipe, id: string): Recipe {
    const coercedSections = coerceInstructionSections(data.instructionSections);
    return {
      id: id,
      title: data.title,
      image: fixImagePath(data.image) || '',
      servings: data.servings,
      servingsDisplay: data.servingsDisplay,
      readyInMinutes: data.readyInMinutes,
      cookingMinutes: data.cookingMinutes,
      preparationMinutes: data.preparationMinutes,
      handsOnMinutes: data.handsOnMinutes,
      readyInMinutesMin: data.readyInMinutesMin,
      statedReadyInMinutes: data.statedReadyInMinutes,
      timeline: data.timeline,
      timelineStatus: data.timelineStatus,
      sourceUrl: data.sourceUrl,
      originalSourceUrl: (data as any).originalSourceUrl,
      sourceName: data.sourceName,
      summary: data.summary || data.description || '',

      // Extended ingredients (heals legacy string-only photo imports)
      extendedIngredients: this.normalizeExtendedIngredients(data),

      // Instructions (flat format; heals legacy string-array photo imports)
      instructions: this.normalizeInstructions(data.instructions),

      // Sectioned instructions with headers.
      // Coerced because two writers persisted the scraper's `steps: string[]`
      // DTO verbatim (#595) — without this those sections render empty.
      // Coercion returning nothing (all sections stepless) falls through to the
      // same client-side reconstruction used for older Firestore recipes that
      // predate instructionSections.
      instructionSections: coercedSections.length
        ? coercedSections
        : reconstructSections(this.normalizeInstructions(data.instructions)),

      // Sectioned ingredients with headers ("For the Pork" / "For the Sauce").
      // Without carrying this through, the field written by enrich/scrape is
      // dropped on read and grouped recipes render as one flat list — the
      // "duplicate ingredients" bug this maps back into groups.
      ingredientGroups: data.ingredientGroups,

      // Which ingredients each step uses, resolved once at import. Dropping it
      // here would silently send every recipe back to the string matcher.
      stepIngredients: data.stepIngredients,

      // Simple ingredients list
      ingredients: data.ingredients,

      // The language the recipe was written in. Dropping it here is what left
      // French recipes rendering inside an English app: the detail read path
      // returned `undefined`, translateRecipe defaulted the source language to
      // 'en', decided source === target, and returned the recipe untranslated
      // without an API call or an error. Both index read paths above already
      // carry this field — same class of bug as ingredientGroups below.
      originalLanguage: data.originalLanguage,

      // Categories
      cuisines: data.cuisines || [],
      dishTypes: data.dishTypes || [],
      diets: data.diets || [],
      occasions: [],

      // Nutrition (RecipeNutrition type has direct properties)
      nutrition: data.nutrition
        ? {
            calories: data.nutrition.calories || 0,
            protein: data.nutrition.protein || 0,
            fat: data.nutrition.totalFat || 0,
            carbs: data.nutrition.totalCarbohydrate || 0,
          }
        : undefined,

      // Video fields
      videoUrl: data.videoUrl,
      videoPlatform: data.videoPlatform,
      videoThumbnail: data.videoThumbnail,

      // Source rating (set by scraper)
      sourceRating: data.sourceRating,

      // Last scraped timestamp (used by shouldScrape guard)
      lastScrapedAt: data.lastScrapedAt,
      // Background enrichment attempts (#668). The server writes this counter;
      // dropping it here left the client's retry cap permanently at zero.
      enrichmentAttemptCount: (data as any).enrichmentAttemptCount,

      // Collection-import review workflow. These must be carried through here
      // or the import-review screen reads every recipe back with no status and
      // silently renders empty — the same class of bug as ingredientGroups above.
      reviewStatus: data.reviewStatus,
      importBatchId: data.importBatchId,
      importBatchTitle: data.importBatchTitle,
      importBatchSourceUrl: data.importBatchSourceUrl,
      importedVia: data.importedVia,
      publishedAt: data.publishedAt,

      // Other defaults
      cheap: data.isBaseRecipe ? false : undefined,
      lowFodmap: data.isBaseRecipe ? false : undefined,
      sustainable: data.isBaseRecipe ? false : undefined,
      ketogenic: data.isBaseRecipe ? false : undefined,
      whole30: data.isBaseRecipe ? false : undefined,
      creditsText: data.sourceName || '',
    };
  }

  /**
   * Save a recipe to user's collection (with duplicate check)
   */
  async saveRecipe(userId: string, recipe: SharedRecipe): Promise<SaveRecipeResult> {
    if (!isFirebaseEnabled() || !firestore) {
      return { saved: false, error: 'Firebase not enabled' };
    }

    try {
      const normalized = normalizeTitle(recipe.title);

      // Check for duplicates in shared collection
      const sharedDup = await this.findByNormalizedTitle('recipes', normalized);
      if (sharedDup) {
        return { saved: false, duplicate: sharedDup };
      }

      // Check for duplicates in user's saved recipes
      const personalDup = await this.findByNormalizedTitle(
        this.getSavedRecipesCollectionPath(userId),
        normalized
      );
      if (personalDup) {
        return { saved: false, duplicate: personalDup };
      }

      // Save to user's collection
      const userRecipe: UserSavedRecipe = {
        ...recipe,
        normalizedTitle: normalized,
        savedAt: new Date().toISOString(),
        source: 'chrome-extension',
        userId,
      };

      const recipeId = recipe.id || `saved-${Date.now()}`;
      await setDoc(getSavedRecipeDocRef(userId, recipeId), deepRemoveUndefined(userRecipe));

      // Invalidate cache
      this.userRecipesCache.delete(userId);

      console.log(`UnifiedRecipeService - Saved recipe: ${recipe.title}`);
      return { saved: true };
    } catch (error) {
      console.error('UnifiedRecipeService - Error saving recipe:', error);
      return { saved: false, error: String(error) };
    }
  }

  /**
   * Find recipe by normalized title in a collection
   */
  private async findByNormalizedTitle(
    collectionPath: string,
    normalizedTitle: string
  ): Promise<RecipeIndexEntry | null> {
    if (!isFirebaseEnabled() || !firestore) {
      return null;
    }

    try {
      const recipesRef = collection(firestore, collectionPath);
      const q = query(
        recipesRef,
        where('normalizedTitle', '==', normalizedTitle),
        limit(1)
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        const data = doc.data() as SharedRecipe;
        return {
          id: doc.id,
          title: data.title,
          image: data.image,
          servings: data.servings,
          servingsDisplay: data.servingsDisplay,
          readyInMinutes: data.readyInMinutes,
          handsOnMinutes: data.handsOnMinutes,
          readyInMinutesMin: data.readyInMinutesMin,
          dishTypes: data.dishTypes || [],
          cuisines: data.cuisines || [],
          diets: data.diets || [],
          ingredients: indexIngredientLines(data),
          searchText: data.searchText || '',
        };
      }

      return null;
    } catch (error) {
      console.error('UnifiedRecipeService - Error finding by title:', error);
      return null;
    }
  }

  /**
   * Hide a recipe (for shared recipes the user doesn't want to see)
   */
  async hideRecipe(userId: string, recipeId: string): Promise<boolean> {
    if (!isFirebaseEnabled() || !firestore) {
      return false;
    }

    try {
      const prefsRef = getRecipePreferencesDocRef(userId);
      await setDoc(
        prefsRef,
        { hidden: arrayUnion(recipeId) },
        { merge: true }
      );

      // Invalidate cache
      this.preferencesCache.delete(userId);

      return true;
    } catch (error) {
      console.error('UnifiedRecipeService - Error hiding recipe:', error);
      return false;
    }
  }

  /**
   * Unhide a recipe
   */
  async unhideRecipe(userId: string, recipeId: string): Promise<boolean> {
    if (!isFirebaseEnabled() || !firestore) {
      return false;
    }

    try {
      const prefsRef = getRecipePreferencesDocRef(userId);
      await updateDoc(prefsRef, { hidden: arrayRemove(recipeId) });

      // Invalidate cache
      this.preferencesCache.delete(userId);

      return true;
    } catch (error) {
      console.error('UnifiedRecipeService - Error unhiding recipe:', error);
      return false;
    }
  }

  /**
   * Update a recipe (works for user's saved recipes and shared recipes)
   * For shared recipes, the update is saved to user's savedRecipes as an override
   */
  async updateRecipe(
    userId: string,
    recipeId: string,
    updates: Partial<Recipe>
  ): Promise<boolean> {
    if (!isFirebaseEnabled() || !firestore) {
      return false;
    }

    try {
      // Check if recipe exists in user's saved recipes
      const savedRef = getSavedRecipeDocRef(userId, recipeId);
      const savedSnap = await getDoc(savedRef);
      // A recipe the cook has not saved yet is copied from the shared catalogue.
      const sharedRef = doc(firestore, 'recipes', recipeId);
      const sharedSnap = savedSnap.exists() ? null : await getDoc(sharedRef);
      const stored = savedSnap.exists() ? savedSnap.data() : sharedSnap?.exists() ? sharedSnap.data() : undefined;

      // Convert Recipe updates to Firestore format
      const firestoreUpdates: Record<string, any> = {};

      if (updates.title !== undefined) {
        firestoreUpdates.title = updates.title;
        firestoreUpdates.normalizedTitle = normalizeTitle(updates.title);
        firestoreUpdates.searchText = updates.title.toLowerCase();
      }
      if (updates.sourceName !== undefined) firestoreUpdates.sourceName = updates.sourceName;
      if (updates.sourceUrl !== undefined) firestoreUpdates.sourceUrl = updates.sourceUrl;
      if (updates.summary !== undefined) firestoreUpdates.summary = updates.summary;
      if (updates.image !== undefined) firestoreUpdates.image = updates.image;
      if (updates.preparationMinutes !== undefined) firestoreUpdates.preparationMinutes = updates.preparationMinutes;
      if (updates.cookingMinutes !== undefined) firestoreUpdates.cookingMinutes = updates.cookingMinutes;
      if (updates.readyInMinutes !== undefined) firestoreUpdates.readyInMinutes = updates.readyInMinutes;
      if (updates.handsOnMinutes !== undefined) firestoreUpdates.handsOnMinutes = updates.handsOnMinutes;
      if (updates.readyInMinutesMin !== undefined) firestoreUpdates.readyInMinutesMin = updates.readyInMinutesMin;
      if (updates.timelineStatus !== undefined) firestoreUpdates.timelineStatus = updates.timelineStatus;
      // Who stated the time is the caller's to say: the editor sends 'person'. It was
      // inferred from the minutes, which made the scrape heal's re-read time a cook's.
      if (updates.timeSource !== undefined) firestoreUpdates.timeSource = updates.timeSource;
      if (updates.servings !== undefined) firestoreUpdates.servings = updates.servings;
      if (updates.dishTypes !== undefined) firestoreUpdates.dishTypes = updates.dishTypes;

      // Handle nutrition updates (RecipeNutrition has direct calories property)
      if (updates.nutrition) {
        if (updates.nutrition.calories !== undefined) {
          firestoreUpdates['nutrition.calories'] = updates.nutrition.calories;
        }
        if (updates.nutrition.protein !== undefined) {
          firestoreUpdates['nutrition.protein'] = updates.nutrition.protein;
        }
        if (updates.nutrition.fat !== undefined) {
          firestoreUpdates['nutrition.fat'] = updates.nutrition.fat;
        }
        if (updates.nutrition.carbs !== undefined) {
          firestoreUpdates['nutrition.carbs'] = updates.nutrition.carbs;
        }
      }

      // Handle instructions updates
      if (updates.instructions?.length) {
        firestoreUpdates.instructions = updates.instructions.map((step, idx) => ({
          number: step.number || idx + 1,
          step: step.step,
        }));
      }

      // Handle ingredients updates
      if (updates.extendedIngredients) {
        // Untouched lines are written back as the document holds them, never
        // from the copy on screen (#792).
        firestoreUpdates.extendedIngredients = ingredientRowsToStore(
          updates.extendedIngredients,
          stored?.extendedIngredients,
        );
        // Also update simple ingredients list
        firestoreUpdates.ingredients = updates.extendedIngredients.map(ing => ing.original);
      } else if (updates.ingredients) {
        firestoreUpdates.ingredients = updates.ingredients;
      }

      if (updates.lastScrapedAt !== undefined) firestoreUpdates.lastScrapedAt = updates.lastScrapedAt;

      // The words a translation is made from changed: the server translates the
      // recipe again (translate at write). Times, photo or tags alone do not.
      const rewordsRecipe =
        updates.title !== undefined || !!updates.extendedIngredients || !!updates.ingredients ||
        !!updates.instructions?.length || updates.summary !== undefined;

      // A recipe with no language is never translated by the planner or the
      // grocery list. A hand-written draft only gets its words here, and a recipe
      // saved before the language was stamped gets one on its next edit.
      if (stored?.originalLanguage !== 'en' && stored?.originalLanguage !== 'fr') {
        const lines = (rows: unknown): string[] =>
          Array.isArray(rows)
            ? rows.map(r => (typeof r === 'string' ? r : (r as any)?.step ?? (r as any)?.original ?? ''))
            : [];
        Object.assign(firestoreUpdates, recipeLanguageField([
          firestoreUpdates.title ?? stored?.title,
          ...lines(firestoreUpdates.ingredients ?? stored?.ingredients ?? stored?.extendedIngredients),
          ...lines(firestoreUpdates.instructions ?? stored?.instructions),
        ]));
      }
      firestoreUpdates.updatedAt = new Date().toISOString();

      // A translated title is a translation of the old title. Kept through a
      // rename, the card of a reader in the other language would go on showing
      // the old name; dropped, the nightly saved-title sweep makes a new one.
      const renamed = (previous: unknown) =>
        updates.title !== undefined && String(previous ?? '').trim() !== updates.title.trim();

      if (savedSnap.exists()) {
        // Update existing saved recipe
        if (renamed(savedSnap.data()?.title)) firestoreUpdates.translatedTitles = deleteField();
        await updateDoc(savedRef, firestoreUpdates);
        if (rewordsRecipe) requestTranslationAtWrite(recipeId, 'edit');
      } else {
        // Recipe is from shared collection - create a copy in user's saved recipes
        if (sharedSnap?.exists()) {
          // Merge shared recipe data with updates
          const sharedData = sharedSnap.data();
          const mergedData = {
            ...sharedData,
            ...firestoreUpdates,
            ...(renamed(sharedData.title) && { translatedTitles: undefined }),
            savedAt: new Date().toISOString(),
            source: 'app',
            userId,
          };
          await setDoc(savedRef, deepRemoveUndefined(mergedData));
          // A fork that says something else than the catalogue keeps its own
          // language (the server answers differs-from-source); one that does not is fresh.
          if (rewordsRecipe) requestTranslationAtWrite(recipeId, 'edit');
        } else {
          // Recipe doesn't exist anywhere
          console.error('UnifiedRecipeService - Recipe not found:', recipeId);
          return false;
        }
      }

      // Invalidate cache
      this.userRecipesCache.delete(userId);
      this.sharedRecipesCache = null;

      console.log(`UnifiedRecipeService - Updated recipe: ${recipeId}`);
      return true;
    } catch (error) {
      console.error('UnifiedRecipeService - Error updating recipe:', error);
      return false;
    }
  }

  /**
   * Delete a recipe (only works for user's saved recipes)
   * Throws an error if deletion fails - caller should handle errors
   *
   * A recipe's id is referenced from five other collections (planned meals,
   * ratings/notes, cook log, hidden/favourite lists, grocery items). This used
   * to delete only the recipe document, leaving every one of them dangling —
   * a planned meal that could never rehydrate, a rating attached to nothing.
   *
   * The recipe document goes first and is the only thing awaited; the
   * references are swept after it, and `references` says how that went. Until
   * #793 the sweep came first, ~20 round trips the cook waited on with the
   * recipe still on screen — 30 s on a phone, long enough to report a bug.
   * A sweep that never finishes (a failure, a closed tab) leaves only ids that
   * point at nothing: a planned meal for a deleted recipe is removed by the
   * planner itself (#777), and the rest are invisible.
   */
  async deleteRecipe(
    userId: string,
    recipeId: string
  ): Promise<{ references: Promise<ReferenceCleanupResult | null> }> {
    if (!isFirebaseEnabled() || !firestore) {
      throw new Error('Firebase is not available');
    }

    try {
      await deleteDoc(getSavedRecipeDocRef(userId, recipeId));
    } catch (error) {
      console.error('UnifiedRecipeService - Error deleting recipe:', error);
      throw new Error('Failed to delete recipe from database');
    }

    // Clear ALL relevant caches to ensure consistent state
    this.userRecipesCache.delete(userId);
    this.recipeDetailCache.delete(recipeId);
    // A read still in flight (a prefetch) would cache the deleted recipe.
    this.detailGeneration += 1;
    // Also invalidate the shared cache since recipe lists need refresh
    this.sharedRecipesCache = null;
    this.lastCacheTime = 0;

    // Both scopes, not just the active one: a paired user's metadata lives
    // under the household even for recipes still held in their personal
    // collection, so sweeping only one leaves the other behind.
    const references = purgeRecipeReferences(
      resolveAllUserScopes(userId, getActiveHouseholdId()),
      recipeId
    ).catch((cleanupError) => {
      console.error(
        'UnifiedRecipeService - Failed to purge references after delete:',
        cleanupError
      );
      return null;
    });

    return { references };
  }

  /**
   * Toggle favorite status for a recipe
   */
  async toggleFavorite(userId: string, recipeId: string): Promise<boolean> {
    if (!isFirebaseEnabled() || !firestore) {
      return false;
    }

    try {
      const prefs = await this.getUserPreferences(userId);
      const isFavorite = prefs.favorites.includes(recipeId);

      const prefsRef = getRecipePreferencesDocRef(userId);
      if (isFavorite) {
        await setDoc(prefsRef, { favorites: arrayRemove(recipeId) }, { merge: true });
      } else {
        await setDoc(prefsRef, { favorites: arrayUnion(recipeId) }, { merge: true });
      }

      // Invalidate cache
      this.preferencesCache.delete(userId);

      return !isFavorite; // Return new state
    } catch (error) {
      console.error('UnifiedRecipeService - Error toggling favorite:', error);
      return false;
    }
  }

  /**
   * Check for duplicates by normalized title (for API endpoint)
   */
  async checkDuplicate(
    userId: string,
    title: string
  ): Promise<{ isDuplicate: boolean; existing?: RecipeIndexEntry }> {
    const normalized = normalizeTitle(title);

    // Check shared recipes
    const sharedDup = await this.findByNormalizedTitle('recipes', normalized);
    if (sharedDup) {
      return { isDuplicate: true, existing: sharedDup };
    }

    // Check user's saved recipes
    const personalDup = await this.findByNormalizedTitle(
      this.getSavedRecipesCollectionPath(userId),
      normalized
    );
    if (personalDup) {
      return { isDuplicate: true, existing: personalDup };
    }

    return { isDuplicate: false };
  }

  /**
   * Recover a full shared-catalog recipe by its (normalized) title. Used by the
   * detail screen to backfill a missing `sourceUrl` (and, opportunistically,
   * instructions) for a saved/imported recipe that lost its source link: without
   * a `sourceUrl` the self-heal scrape can never fire, leaving the recipe stuck
   * on the "Aucune instruction ajoutée" dead-end (#485). Returns null when no
   * shared recipe matches the title.
   */
  async getSharedRecipeByTitle(title: string): Promise<Recipe | null> {
    if (!isFirebaseEnabled() || !firestore || !title?.trim()) return null;
    try {
      const match = await this.findByNormalizedTitle('recipes', normalizeTitle(title));
      if (!match) return null;
      return this.getRecipeById(match.id);
    } catch (error) {
      console.warn('UnifiedRecipeService - getSharedRecipeByTitle failed (non-critical):', error);
      return null;
    }
  }
}

export const unifiedRecipeService = new UnifiedRecipeService();
export default unifiedRecipeService;
